import { computed, watchEffect } from 'vue';

import { useThreeObject } from '@resn/gozer-vue';

import { getAllMeshes } from '../../utils';
import { usePack } from '../Pack.vue';
import { PackTiers } from '~/core/constants';
import { useGlobalAssets } from '~/providers/GlobalAssets';

export const overrideProps = {
    metalness: 0.8,
    roughness: 0.2,
    map: null,
    envMapIntensity: 1.5,
};

const overrideNormalScales = {
    baseball: 0.8,
    basketball: 0.25,
    football: 0.8,
    pokemon: 1,
};

export const useSceneObject = ({ object, props, name, objectProps }) => {
    const v = computed(() => props.visible);
    const overrideNormalScale = overrideNormalScales[name.toLowerCase()] || 0.5;

    const meshes = getAllMeshes(object).filter((mesh) => !mesh.ignoreOverride);

    const pack = usePack();

    const isOverride = computed(() => pack.tier.value > 1);
    const isIrridescent = computed(() => pack.tier.value === 4);

    const color = computed(() => (isOverride.value ? PackTiers[pack.tier.value].color : 'white'));

    useThreeObject(object, { name, addToParent: false, props: { v, ...objectProps } });

    meshes.forEach((mesh) => {
        const { metalness, roughness, map, envMapIntensity, normalScale, color } = mesh.material;
        const c = `#${color.getHexString()}`;

        mesh.baseProps = {
            metalness,
            roughness,
            map,
            envMapIntensity,
            color,
        };
        mesh.overrideProps = { ...overrideProps, ...mesh.overrideProps };

        mesh.iridescentProps = {
            color: c,
            normalScale: normalScale.x,
        };

        mesh.baseMaterial = mesh.material;

        mesh.layers.set(props.layer);
    });

    useGlobalAssets(({ data }) => {
        const { matcapIrridescent } = data;

        meshes.forEach((mesh) => {
            mesh.material.tMatcap = matcapIrridescent;
        });
    });

    watchEffect(() => {
        meshes.forEach((mesh) => {
            if (mesh.ignoreUpdates) return;

            const baseColor = mesh.iridescentProps.color;
            const c = isOverride.value ? color.value : baseColor;

            if (mesh.userData.isReflective && isIrridescent.value) {
                mesh.material.color.set('#5d41ff');
            } else {
                mesh.material.color.set(c);
            }
        });
    });

    watchEffect(() => {
        meshes.forEach((mesh) => {
            if (mesh.ignoreUpdates) return;

            const props = isOverride.value ? mesh.overrideProps : mesh.baseProps;
            Object.assign(mesh.material, props);
            mesh.material.needsUpdate = true;

            const normalScale = isOverride.value
                ? overrideNormalScale
                : mesh.iridescentProps.normalScale;

            mesh.material.normalScale.setScalar(normalScale);
            mesh.material.bumpScale = normalScale;
            mesh.material.uOverride = isOverride.value;
            mesh.material.uFresnelStr = isOverride.value ? 0.5 : 0;
        });
    });

    watchEffect(() => {
        meshes.forEach((mesh) => {
            if (mesh.ignoreUpdates) return;

            mesh.material.uUseMatcap = isIrridescent.value;
            mesh.material.uFresnelColor.set(isIrridescent.value ? '#4715ac' : '#fff');
            mesh.material.uFresnelPower = isIrridescent.value ? 1.2 : 6;

            if (mesh.userData.isReflective) {
                mesh.material.uUseMatcap = false;
            }
        });
    });

    return { color, isOverride };
};
